/**
 * 全局字段档位（`globalsProfile`）—— 见 doc/开发标准/analytics_事件参数治理方案_初稿_2026-10-05.md §4.3 / §5 阶段 2。
 *
 * 背景：GA4 单事件总参数硬上限 36（= 自有 ≤25 + GA4 自动 7~11），超限**静默丢弃**。
 * 全局字段（timestamp / environment / user_agent / browser_language / timezone）会追加在
 * 每个事件上，轻事件白占额度、重事件直接把参数挤爆。
 *
 * 做法：按**事件定义声明的自有参数个数**（静态，禁止运行时动态）选一个档位，
 * 档位决定自动附带哪些全局字段：
 *
 *   full     5 个（timestamp / environment / user_agent / browser_language / timezone）  own ≤ 7
 *   standard 3 个（timestamp / environment / user_agent）                                8 ≤ own ≤ 15
 *   minimal  2 个（timestamp / environment）                                             own ≥ 16
 *
 * ⚠️ 另有一个**恒发**标记 `globals_profile`（= 当前档位名），它本身占 1 个参数槽，
 * 因此「声明峰值 = own + 该档全局字段数 + 1」。
 * 为什么必须发：否则分析侧无法区分「这个字段是**设计性缺席**（档位没带）」与
 * 「**带了但被丢**（截断）」，会重演「字段缺失被误读为页面未隐藏」那类错误（§4.6 硬约束 2）。
 *
 * ⚠️ 本文件**必须保持零 import**：静态门 `scripts/check-analytics-param-budget.js` 用
 * `data:` URL 直接加载它来算档位与峰值（避免门与实现各写一份档位表而漂移）。
 *
 * ⚠️ 三条硬约束（§4.6）：
 *   1. 档位只由**声明**决定 —— 禁止按「本次实际传了几条」动态裁剪（同事件不同实例会带不同
 *      字段集 ⇒ 破坏可分析性）。
 *   2. 必须显式上报 `globals_profile`。
 *   3. 「声明集合」≠「单实例峰值」：`optionalParams` 是全集、含条件参数，故本档位是
 *      **保守估计**（只会让重事件偏向更轻的档，不会误判成轻事件）。
 */

export const GLOBALS_PROFILE = Object.freeze({
  FULL: 'full',
  STANDARD: 'standard',
  MINIMAL: 'minimal'
});

/** 档位标记字段名：恒发（每个事件都带），用于区分「设计性缺席」与「被截断丢弃」 */
export const GLOBALS_PROFILE_MARKER_KEY = 'globals_profile';

/** 各档自动附带的全局字段（顺序即注入顺序；`timestamp` / `environment` 在每一档里都在，属 P0 保护） */
export const GLOBALS_BY_PROFILE = Object.freeze({
  full: Object.freeze(['timestamp', 'environment', 'user_agent', 'browser_language', 'timezone']),
  standard: Object.freeze(['timestamp', 'environment', 'user_agent']),
  minimal: Object.freeze(['timestamp', 'environment'])
});

export const GLOBALS_PROFILE_DEFAULT = GLOBALS_PROFILE.FULL;

/**
 * 档位阈值，按**自有参数个数**（requiredParams ∪ optionalParams 的去重条数）划档。
 * 边界（实测于 2026-10-05，160 个事件定义）：own 7/8/15/16 ⇒ 峰值 13/12/19/19，单调不越界。
 */
export const GLOBALS_PROFILE_THRESHOLDS = Object.freeze({
  fullMaxOwnParams: 7,
  standardMaxOwnParams: 15
});

export function isGlobalsProfile(value) {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(GLOBALS_BY_PROFILE, value);
}

/** 事件定义声明的自有参数个数（requiredParams ∪ optionalParams 去重） */
export function countDeclaredParams(event) {
  if (!event || typeof event !== 'object') return 0;

  const keys = new Set();
  for (const list of [event.requiredParams, event.optionalParams]) {
    if (!Array.isArray(list)) continue;
    for (const key of list) {
      if (typeof key === 'string' && key) keys.add(key);
    }
  }
  return keys.size;
}

/**
 * 解析档位：显式声明优先（事件定义上的 `globalsProfile`），否则按自有参数个数派生。
 * 非法/缺失的 override 一律**回退到派生结果**（不抛错，埋点不得影响主链路）。
 */
export function resolveGlobalsProfile(ownCount, override) {
  if (isGlobalsProfile(override)) return override;

  const own = Number.isFinite(ownCount) && ownCount > 0 ? Math.floor(ownCount) : 0;
  if (own <= GLOBALS_PROFILE_THRESHOLDS.fullMaxOwnParams) return GLOBALS_PROFILE.FULL;
  if (own <= GLOBALS_PROFILE_THRESHOLDS.standardMaxOwnParams) return GLOBALS_PROFILE.STANDARD;
  return GLOBALS_PROFILE.MINIMAL;
}

export function countGlobalsForProfile(profile) {
  const list = GLOBALS_BY_PROFILE[profile];
  return Array.isArray(list) ? list.length : 0;
}

/** 所有档位出现过的全局字段并集（静态门用它做「新增全局字段但没登记进任何档位」的漂移检查） */
export function listAllProfileGlobalKeys() {
  const keys = new Set();
  for (const list of Object.values(GLOBALS_BY_PROFILE)) {
    for (const key of list) keys.add(key);
  }
  return [...keys];
}

/** 声明峰值 = 自有 + 该档全局字段数 + 1（`globals_profile` 标记本身） */
export function resolveDeclaredPeak(ownCount, override) {
  const own = Number.isFinite(ownCount) && ownCount > 0 ? Math.floor(ownCount) : 0;
  const profile = resolveGlobalsProfile(own, override);
  const globalsCount = countGlobalsForProfile(profile);
  return { profile, ownCount: own, globalsCount, peak: own + globalsCount + 1 };
}
